import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import type { Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/store/auth/auth.store';
import { getDeviceInfo } from '@/store/notification-settings/device-info';
import {
  useCreateNotificationMethodMutation,
  useNotificationMethodsQuery,
} from '@/store/notification-settings/notification-settings.store';
import { NotificationMethodType } from '@/store/notification-settings/notification-settings.types';
import { redirectFromNotification } from '@/utils/notification-redirect';

export function usePushNotifications() {
  const navigate = useNavigate();
  const { mutateAsync } = useCreateNotificationMethodMutation();
  // Layout calls this before it redirects a signed-out visitor. Ungated, the request goes out with
  // no token, 401s, the refresh fails and api.ts hard-reloads /auth a second time.
  const isLoggedIn = useAuth((s) => s.isLoggedIn);
  const { data: methods } = useNotificationMethodsQuery(undefined, isLoggedIn);

  // Web: the service worker (firebase-messaging-sw.js) renders background push
  // notifications and, on click, posts a PUSH_NAVIGATE message to an already-open
  // tab. Handle it here so the tab switches service context and navigates.
  // (A cold-start click has no open tab, so the SW opens a fresh window with the
  // link + ?service_id= instead, which Layout's deep-link handler picks up.)
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    if (!('serviceWorker' in navigator)) return;

    const onMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'PUSH_NAVIGATE' || !event.data.url) return;
      void redirectFromNotification({ link: event.data.url }, navigate);
    };

    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);

  // Native (Android/iOS): handle notification taps → redirect. Registered on its own,
  // independent of the token/methods query, so a cold-start launch tap (app killed,
  // opened by tapping the push) isn't missed while auth + methods are still loading.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const actionListener = PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (action: ActionPerformed) => {
        // Backend push payload: data: { link, service_id }. Switch to the
        // originating service context and redirect to the target page.
        void redirectFromNotification(
          (action.notification?.data ?? {}) as { link?: string; service_id?: string },
          navigate,
        );
      },
    );

    return () => {
      actionListener.then((l) => l.remove());
    };
  }, [navigate]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (!methods) return;

    const registerNotifications = async () => {
      const permission = await PushNotifications.requestPermissions();

      if (permission.receive === 'granted') {
        await PushNotifications.register();
      } else {
        console.warn('Push notification permission denied');
      }
    };

    const registrationListener = PushNotifications.addListener(
      'registration',
      async (token: Token) => {
        const exists = methods.some(
          ({ data, type }) => type === NotificationMethodType.PUSH && data === token.value,
        );
        if (exists) return;
        try {
          const { deviceId, deviceName } = await getDeviceInfo();
          await mutateAsync({
            type: NotificationMethodType.PUSH,
            data: token.value,
            deviceId,
            deviceName,
          });
        } catch (err) {
          console.error('Failed to register push token:', err);
        }
      },
    );

    const registrationErrorListener = PushNotifications.addListener(
      'registrationError',
      (error: any) => {
        console.error('Registration error:', error);
      },
    );

    const notificationReceivedListener = PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        console.log('Push received:', notification);
      },
    );

    registerNotifications();

    return () => {
      registrationListener.then((l) => l.remove());
      registrationErrorListener.then((l) => l.remove());
      notificationReceivedListener.then((l) => l.remove());
    };
  }, [methods, mutateAsync]);
}
